import { useEffect } from 'react'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import {
  discountModeToApplyEachPayment,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from '../../../../../discountMode.utils'
import {
  getStreamCustomPeriodBasePrice,
  getStreamCustomPeriodSuggestedPrice,
  isStreamCustomPeriodLinkTariff,
  type MonthlyCourseLinkPeriodUnit,
} from '../../../../../monthlyCourseLinkDiscount.utils'
import type { ICourseEnrollmentItem } from '../types'

type OnChange = (
  key: keyof ICourseEnrollmentItem,
  value: number | string | boolean | null | object,
) => void

type UseStreamAdditionalCourseFormParams = {
  order: ICourseEnrollmentItem
  selectedTariff?: ICourseTariff | null
  defaultDiscount?: number | null
  onChange: OnChange
}

const getDefaultStreamPeriodUnit = (
  tariffType?: string | null,
): MonthlyCourseLinkPeriodUnit =>
  tariffType === 'stream_custom_days' ? 'day' : 'month'

/**
 * Оркестрация periodного доп. курса (stream_*): discount_mode + фиксация периода.
 */
export const useStreamAdditionalCourseForm = ({
  order,
  selectedTariff,
  defaultDiscount = null,
  onChange,
}: UseStreamAdditionalCourseFormParams) => {
  const isActive = isStreamCustomPeriodLinkTariff(selectedTariff?.type)
  const isPeriodFixed = Boolean(order.is_period_fixed)
  const periodUnit: MonthlyCourseLinkPeriodUnit =
    order.period_unit === 'day'
      ? 'day'
      : getDefaultStreamPeriodUnit(selectedTariff?.type)
  const periodValue =
    order.value != null &&
    Number.isFinite(Number(order.value)) &&
    Number(order.value) >= 1
      ? Math.trunc(Number(order.value))
      : null
  const discountValue = Math.max(0, Math.trunc(Number(order.discount) || 0))
  const discountMode = resolveCrmLinkDiscountMode({
    discount: discountValue,
    discountMode: order.discount_mode,
    applyDiscountEachPayment: order.apply_discount_each_payment,
    isPeriodFixed,
  })
  const streamPeriodValue =
    periodValue ??
    (isActive && selectedTariff ? getTariffValueMin(selectedTariff) : null)
  const streamBasePrice = getStreamCustomPeriodBasePrice(
    selectedTariff,
    streamPeriodValue,
  )
  const streamSuggestedPrice = getStreamCustomPeriodSuggestedPrice(
    selectedTariff,
    streamPeriodValue,
    discountValue,
    order.discount_mode,
    order.apply_discount_each_payment,
    isPeriodFixed,
  )

  useEffect(() => {
    if (!isActive || !selectedTariff) return

    if (discountValue <= 0 && (order.apply_discount_each_payment || order.discount_mode)) {
      onChange('apply_discount_each_payment', false)
      onChange('discount_mode', null)
    }

    if (isPeriodFixed) {
      if (!order.period_unit) {
        onChange('period_unit', getDefaultStreamPeriodUnit(selectedTariff.type))
      }
    } else if (order.period_unit != null) {
      onChange('period_unit', null)
    }

    if (!isPeriodFixed && (!order.value || Number(order.value) < 1)) {
      onChange('value', getTariffValueMin(selectedTariff) || 1)
    }

    if (isPeriodFixed) return

    if (order.price !== streamSuggestedPrice) {
      onChange('price', streamSuggestedPrice)
    }
  }, [
    isActive,
    selectedTariff,
    isPeriodFixed,
    order.period_unit,
    order.value,
    order.price,
    order.apply_discount_each_payment,
    order.discount_mode,
    discountValue,
    streamSuggestedPrice,
    onChange,
  ])

  const onTogglePeriodFixed = (checked?: boolean) => {
    if (!selectedTariff) return

    const nextChecked =
      typeof checked === 'boolean' ? checked : !isPeriodFixed
    const nextUnit = getDefaultStreamPeriodUnit(selectedTariff.type)
    onChange('is_period_fixed', nextChecked)
    onChange('period_unit', nextChecked ? nextUnit : null)
    const nextValue = Math.max(
      1,
      Math.trunc(Number(order.value) || getTariffValueMin(selectedTariff) || 1),
    )
    onChange('value', nextValue)

    const nextMode: CrmLinkDiscountMode | null =
      discountValue > 0
        ? nextChecked
          ? 'renewal_only'
          : 'first_only'
        : null
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
    onChange(
      'price',
      getStreamCustomPeriodSuggestedPrice(
        selectedTariff,
        nextValue,
        discountValue,
        nextMode,
        false,
        nextChecked,
      ),
    )
  }

  const onChangePeriodUnit = (value: MonthlyCourseLinkPeriodUnit) => {
    onChange('period_unit', value)
  }

  const onChangeValue = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      onChange('value', null)
      return
    }
    onChange('value', Math.max(1, Math.trunc(Number(value) || 0)))
  }

  const onChangeUnfixedValue = (value: number) => {
    onChange('value', Math.max(1, Math.trunc(Number(value) || 1)))
  }

  const onChangeDiscount = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      onChange('discount', null)
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      return
    }
    const nextDiscount = Math.max(0, Math.trunc(Number(value) || 0))
    onChange('discount', nextDiscount || null)
    if (nextDiscount <= 0) {
      onChange('discount_mode', null)
      onChange('apply_discount_each_payment', false)
      return
    }
    const nextMode = resolveCrmLinkDiscountMode({
      discount: nextDiscount,
      discountMode: isPeriodFixed
        ? 'renewal_only'
        : order.discount_mode ?? 'first_only',
      isPeriodFixed,
    })
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
  }

  const onDiscountModeChange = (mode: CrmLinkDiscountMode | null) => {
    const nextMode = resolveCrmLinkDiscountMode({
      discount: discountValue,
      discountMode: mode,
      isPeriodFixed,
    })
    onChange('discount_mode', nextMode)
    onChange(
      'apply_discount_each_payment',
      discountModeToApplyEachPayment(nextMode),
    )
  }

  const onChangePrice = (value: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      onChange('price', null)
      return
    }
    onChange('price', Math.max(0, Number(value) || 0))
  }

  const applyTariffSelection = (tariff: ICourseTariff) => {
    const nextDiscount =
      order.discount === null || order.discount === undefined
        ? defaultDiscount
        : Math.max(0, Math.trunc(Number(order.discount) || 0)) || null
    const nextValue = getTariffValueMin(tariff)
    onChange('discount', nextDiscount)
    onChange('discount_mode', nextDiscount ? 'first_only' : null)
    onChange('value', nextValue)
    onChange('period_unit', null)
    onChange(
      'price',
      getStreamCustomPeriodSuggestedPrice(
        tariff,
        nextValue,
        nextDiscount,
        nextDiscount ? 'first_only' : null,
        false,
        false,
      ),
    )
    onChange('apply_discount_each_payment', false)
    onChange('is_period_fixed', false)
  }

  return {
    isActive,
    isPeriodFixed,
    periodUnit,
    periodValue,
    discountValue,
    discountMode,
    streamPeriodValue,
    streamBasePrice,
    streamSuggestedPrice,
    displayPrice: Number(order.price) || streamSuggestedPrice,
    onTogglePeriodFixed,
    onChangePeriodUnit,
    onChangeValue,
    onChangeUnfixedValue,
    onChangeDiscount,
    onDiscountModeChange,
    onChangePrice,
    applyTariffSelection,
  }
}
